<template>
    <div class="home">
        <covid-announcement v-if="!upToDate" />
        <!-- <div class="sponsorship">
            <p>Presented By</p>
            <lazy-image
                class="sponsor-logo-screen"
                alt="Signature Bank"
                :src="sponsorLogoSrc"
                :srcset="sponsorLogoSrcSet"
                :sizes="sponsorLogoSizes"
            />
            <img
                v-if="showPrintLogo"
                class="sponsor-logo-print"
                alt="Signature Bank"
                :src="sponsorLogoPrintSrc"
            />
        </div>-->

        <h1 v-if="week">
            <span>Broadway Grosses</span>
            <span>
                Week Ending
                <span>{{ week.end | date }} (#{{ week.number }})</span>
            </span>
        </h1>

        <add-this v-if="week" />

        <week-snapshot v-if="week" />

        <season-to-date-chart v-if="week" />

        <week-season-comparison v-if="week" />

        <h2 v-if="week" class="show-by-show-header">Show by Show</h2>

        <grosses-table
            v-if="week"
            :table-data="rows"
            :table-headers="headers"
            :table-sortables="sortables"
            :sort-key="sortKey"
            :sort-direction="sortDirection"
            :searchable="true"
            :clickable="true"
            :is-home="true"
            @rowClick="goToShowDetail"
            @sort="onSort"
        />

        <h2 v-if="week" class="notes-header">Notes</h2>

        <div v-if="week" class="week-notes">
            <div v-html="weekNotes"></div>
        </div>
    </div>
</template>

<script>
import api from 'src/api';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';
import omit from 'lodash/omit';
import GrossesTable from 'src/components/GrossesTable';
import WeekSnapshot from 'src/components/WeekSnapshot';
import WeekSeasonComparison from 'src/components/WeekSeasonComparison';
import SeasonToDateChart from 'src/components/SeasonToDateChart';
import AddThis from 'src/components/AddThis';
import headers from 'src/config/grosses-table-headers';
import { getNewSortKeyAndDirection } from 'src/util/helpers';
import cl from 'src/util/cloudinary';
import { toSrcSet, mediaQueryWidth } from 'src/util/helpers';
import eventBus from 'src/util/eventBus';
import CovidAnnouncement from 'src/components/CovidAnnouncement';

const defaultSort = find(headers, { key: 'show_name' });

export default {
    name: 'historical',
    data() {
        return {
            sortKey: defaultSort.key,
            sortDirection: defaultSort.defaultSort,
            sortCol: defaultSort.sortCol,
            headers: [
                'show_name',
                'past_months',
                'gross_combined',
                'gross_potential_percentage',
                'attendance_combined',
                'capacity_percentage',
                'ticket_average',
            ],
            sortables: [
                'show_name',
                'show_type',
                'gross',
                'gross_potential_percentage',
                'diff_revenue_last_week',
                'diff_attendance_last_week',
                'attendance',
                'capacity_percentage',
                'ticket_average',
                'ticket_min',
                'ticket_max',
                'ticket_top',
            ],
            sponsorLogoWidth: 572,
            sponsorLogoMobileWidth: 375,
            showPrintLogo: false,
        };
    },
    metaInfo() {
        if (this.week) {
            return {
                title: `Grosses Week Ending ${Vue.filter('date')(
                    this.week.end,
                )} (#${this.week.number}) | ${
                    window.broadwayBusiness.config.titleBase
                }`,
            };
        }

        return {
            title: window.broadwayBusiness.config.titleBase,
        };
    },
    components: {
        GrossesTable,
        WeekSnapshot,
        SeasonToDateChart,
        WeekSeasonComparison,
        AddThis,
        CovidAnnouncement,
    },
    created() {
        this.$store.dispatch('week/fetchCurrent');
        this.$store.commit('app/seenHome');

        this.onBeforePrint = () => (this.showPrintLogo = true);

        eventBus.$on('beforeprint', this.onBeforePrint);
    },
    destroyed() {
        eventBus.$off('beforprint', this.onBeforePrint);
    },
    computed: {
        week() {
            return this.$store.getters['week/current'];
        },
        upToDate() {
            return window.broadwayBusiness.upToDate;
        },
        sponsorPlaceholder() {
            return cl.url('grosses/blank', {
                width: this.sponsorLogoWidth,
                height: Math.floor(this.sponsorLogoWidth * 0.157342657),
                fetch_format: 'auto',
                crop: 'scale',
            });
        },
        sponsorLogoSrc() {
            return this.sponsorLogoAtSize(this.sponsorLogoWidth);
        },
        sponsorLogoSrcSet() {
            return toSrcSet({
                [`${this.sponsorLogoMobileWidth}w`]: this.sponsorLogoAtSize(
                    this.sponsorLogoMobileWidth,
                ),
                [`${this.sponsorLogoMobileWidth * 2}w`]: this.sponsorLogoAtSize(
                    this.sponsorLogoMobileWidth * 2,
                ),
                [`${this.sponsorLogoWidth}w`]: this.sponsorLogoAtSize(
                    this.sponsorLogoWidth,
                ),
                [`${this.sponsorLogoWidth * 2}w`]: this.sponsorLogoAtSize(
                    this.sponsorLogoWidth * 2,
                ),
            });
        },
        sponsorLogoSizes() {
            return [
                `${mediaQueryWidth('medium', 'max')} ${
                    this.sponsorLogoMobileWidth
                }px`,
                `${this.sponsorLogoWidth}px`,
            ].join(',');
        },
        sponsorLogoPrintSrc() {
            return this.sponsorLogoAtSize(this.sponsorLogoWidth, {
                effect: 'negate',
            });
        },
        weekNotes() {
            if (!this.week.notes) {
                return '';
            }

            return this.week.notes.split('\n').join('<br>');
        },
        rows() {
            return orderBy(
                this.$store.state.week.grosses,
                this.sortCol,
                this.sortDirection,
            );
        },
    },
    methods: {
        sponsorLogoAtSize(width, params = {}) {
            return cl.url('grosses/signature-bank-logo', {
                width,
                crop: 'scale',
                fetch_format: 'auto',
                ...params,
            });
        },
        goToShowDetail(row) {
            this.$router.push({
                name: 'showDetail',
                params: { show: row.show.slug },
            });
        },
        onSort({ newKey }) {
            const { key, direction } = getNewSortKeyAndDirection(
                newKey,
                this.sortKey,
                this.sortDirection,
            );

            this.sortDirection = direction;
            this.sortKey = key;
            this.sortCol = find(headers, { key: newKey }).sortCol;
        },
    },
};
</script>
